import * as React                from "react";
import {cx}                      from "emotion";
import {FontAwesomeIcon}         from "@fortawesome/react-fontawesome";
import {faCheck}                 from "@fortawesome/free-solid-svg-icons";

import "./login-tab.scss";

import {cst, cstAssign}          from "@Common/react/chain-services";
import {scopedState}             from "@Common/react/scoped-state";
import {Types}                   from "@Common/types";
import {Responsive}              from "@Common/ui-components/responsive";
import {addURLParameterToWindow} from "@Common/utils/add-url-parameter";

import {ModalLayout}             from "@Xpresso/routes/common/modal-layout/modal-layout";
import {FloatingInput}           from "@Xpresso/routes/account/common/floating-labels/floating-input"; 
import {OfficeCreateTabs,
        controlInput}            from "../../office-create-tabs";

export const LoginTab = ({step, office} : {
    step: Types.Common.React.UseState.UseStateNext<string>, 
    office: Types.Xpresso.Account.Admin.OfficeManagement.CreateModal.OfficeState,
}) => cst<{
    responsive: any,
    validation : {
        length: boolean,
        number: boolean,
        lowercase: boolean,
        uppercase: boolean,
        special: boolean,
        matches: boolean,
        full: boolean,
    },
    validationLines: {
        message: string;
        validated: any;
    }[],
}>(
    cstAssign("validation", (_, next) => next({
        length: office.value?.inputs?.userPassword?.length >= 6,
        number: /\d/.test(office.value?.inputs?.userPassword),
        lowercase: office.value?.inputs?.userPassword && /[a-z]/.test(office.value?.inputs?.userPassword),
        uppercase: /[A-Z]/.test(office.value?.inputs?.userPassword),
        special: /[@$!%*#?&]/.test(office.value?.inputs?.userPassword),
        matches: office.value?.inputs?.userPassword && office.value?.inputs?.userPassword === office.value?.inputs?.matchPassword,
        full: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/.test(office.value?.inputs?.userPassword) && office.value?.inputs?.userPassword === office.value?.inputs?.matchPassword
    })),
    cstAssign("validationLines", ({validation}, next) => next(getValidationLines({validation}))),
    cstAssign("responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>),
    ({responsive, validation}, next) => (
        ModalLayout({
            title: "Create New Office",
            content: (
                <>
                    {responsive.ge("md") && OfficeCreateTabs({step})}
                    {next()}
                </>
            ),
            buttons: [
                {
                    label: "Back",
                    onClick: async () => {
                        addURLParameterToWindow({parameter: "step", value: "general"});
                        step.onChange("general");
                    },
                },
                {
                    label: "Next",
                    primary: true,
                    disabled: !validation.full || !!scopedState(office, ['errors', 'userName']).value,
                    onClick: async () => {
                        addURLParameterToWindow({parameter: "step", value: "office-address"});
                        step.onChange("office-address");
                    },
                },
            ]
        })
    ),
    ({validation, validationLines}) => (
        <form className="login-tab-r8d">
            <FloatingInput 
                classContainer="input-card"
                classLabel="label" 
                props={propsUserName(office)}
                textLabel="Username"
            />
            {office.value.errors['userName'] && (
                <div className="errorMessage" xd-qa-type="label" xd-qa-name={office.value.errors['userName']}>{office.value.errors['userName']}</div>
            )}
            <FloatingInput 
                classContainer="input-card"
                classLabel="label" 
                props={propsPassword(office)}
                textLabel="Password"
                type="password"
            />
            <div className="validation-card">
                {validationLines.map((line, i) => (
                    <div className="validation-line" key={i}>
                        {line.validated ? (
                            <div className="validation-icon" xd-qa-type="icon" xd-qa-name={line.message + " Check"}><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: line.validated})} xd-qa-type="label" xd-qa-name={line.message}>{line.message}</div>
                    </div>
                ))}
            </div>
            <FloatingInput 
                classContainer="input-card"
                classLabel="label" 
                props={propsMatchPassword(office)}
                textLabel="Confirm Password"
                type="password"
            />           
            <div className="validation-card">
                <div className="validation-line">
                    {validation.matches ? (
                        <div className="validation-icon" xd-qa-type="icon" xd-qa-name="Passwords Match Check"><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                    ) : (
                        <div className="validation-spacer"/>
                    )}
                    <div className={cx("validation-label", {validated: validation.matches})} xd-qa-type="label" xd-qa-name="Passwords Match">Passwords Match</div>
                </div>
            </div>
        </form>
    )
);


const getValidationLines = ({validation}) => ([
    {
        "message": "At least 6 characters", 
        "validated": validation.length, 
    },
    {
        "message": "At least 1 number", 
        "validated": validation.number, 
    },
    {
        "message": "At least 1 lowercase letter", 
        "validated": validation.lowercase, 
    },
    {
        "message": "At least 1 uppercase letter", 
        "validated": validation.uppercase, 
    },
    {
        "message": "At least 1 special character @$!%*#?&", 
        "validated": validation.special, 
    },
]);

const propsUserName = (office) => ({
    value: scopedState(office, ['inputs', 'userName']).value || controlInput,
    onChange: (e) => {
        const value = e.target.value; 
        scopedState(office, ['errors', 'userName']).onChange(false);
        scopedState(office, ['inputs', 'userName']).onChange(value);
    },
    className: "value",
    maxLength: 60,
    "xd-qa-type": "textbox",
    "xd-qa-name": "Username",
    autoComplete: "username",
})

const propsPassword = (office) => ({
    value: scopedState(office, ['inputs', "userPassword"]).value || controlInput,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value; 
        scopedState(office, ['inputs', "userPassword"]).onChange(value);
    },
    className: "value",
    maxLength: 64,
    "mapping": "userPassword",
    "xd-qa-type": "textBox",
    "xd-qa-name": "Password",
    autoComplete: "new-password",
})

const propsMatchPassword = (office) => ({
    value: scopedState(office, ['inputs', "matchPassword"]).value || controlInput,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value; 
        scopedState(office, ['inputs', "matchPassword"]).onChange(value);
    },
    className: "value",
    maxLength: 64,
    "xd-qa-type": "textbox",
    "xd-qa-name": "Confirm Password",
    autoComplete: "new-password",
})
